import { useCallback, useMemo, useRef } from 'react';

/**
 * Names one scan: from the moment it starts until it is cancelled, rescanned or unmounted.
 * A scan-result poll takes the ticket that is current when it is sent.
 */
export type TScanTicket = { hasSucceeded: boolean };

/**
 * Decides which scan-result responses may change the wizard.
 *
 * Polls that were already in flight when a scan succeeded, was cancelled or was replaced still
 * answer later. Without this gate each late body would put the scan values back over what the
 * customer typed (verifime-adr#685).
 */
export default function useScanResultGate() {
  const currentTicketRef = useRef<TScanTicket | null>(null);

  const beginScan = useCallback(() => {
    currentTicketRef.current = { hasSucceeded: false };
  }, []);

  const endScan = useCallback(() => {
    currentTicketRef.current = null;
  }, []);

  const takeTicket = useCallback(() => currentTicketRef.current, []);

  const isLive = useCallback(
    (ticket: TScanTicket | null) =>
      ticket !== null && ticket === currentTicketRef.current && !ticket.hasSucceeded,
    [],
  );

  /** True once per scan: the first body of the current scan. */
  const acceptBody = useCallback(
    (ticket: TScanTicket | null) => {
      if (!isLive(ticket)) {
        return false;
      }
      ticket.hasSucceeded = true;
      return true;
    },
    [isLive],
  );

  // Stable, so effects can list the gate as a dependency without re-running on every render.
  return useMemo(
    () => ({ beginScan, endScan, takeTicket, isLive, acceptBody }),
    [beginScan, endScan, takeTicket, isLive, acceptBody],
  );
}
